import type { HOAChecklistResponse } from '@/types/HOA';

export const HOA_DUE_DILIGENCE_TOPICS = [
  'OVERVIEW',
  'FINANCIAL',
  'INSURANCE',
  'LEGAL',
  'GOVERNANCE',
  'SITE',
] as const;

export type HOADueDiligenceTopic = (typeof HOA_DUE_DILIGENCE_TOPICS)[number];

export interface HOADueDiligenceQuestion {
  displayText: string;
  prompt: string;
}

const dueDiligencePrompt = (question: string, hasCompiledPackage: boolean): string =>
  [
    `${question}\n\n`,
    'Conduct buyer-side HOA due diligence using the evidence available for this property. ',
    ...(hasCompiledPackage
      ? [
          'Use get_hoa_documents to inspect the relevant logical documents and citations, not merely ',
          'the summary. ',
        ]
      : [
          'This property has separately uploaded HOA documents rather than a compiled package. Rely ',
          'on the structured HOA findings and document inventory already available in the property ',
          'context; do not call get_hoa_documents. ',
        ]),
    'Synthesize rather than inventory: answer in no more than six prioritized findings and about ',
    '450 words. Cross-check sources and identify material conflicts, stale information, assumptions, and ',
    'referenced-but-missing materials. Cite the exact document title and PDF page for every ',
    'material conclusion when available. State uncertainty inline as Confirmed, Indicated but not ',
    'verified, Not found in reviewed documents, or Source document not provided—do not create a ',
    'separate methodology or evidence-status section. Never treat ',
    'silence as proof that a condition does not exist. Do not mention internal tool names, retrieval ',
    'availability, processing architecture, or fallback behavior in the answer. Begin with the ',
    'substantive conclusion. Omit lower-value details before compressing or abandoning a sentence, ',
    'citation, or conclusion. End with the most concrete item to verify before the buyer contingency ',
    'expires.',
  ].join('');

const question = (
  displayText: string,
  inquiry: string,
  hasCompiledPackage: boolean,
): HOADueDiligenceQuestion => ({
  displayText,
  prompt: dueDiligencePrompt(inquiry, hasCompiledPackage),
});

const pluralize = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

export function buildHOADueDiligenceQuestions(
  data: HOAChecklistResponse,
  topic: HOADueDiligenceTopic,
): HOADueDiligenceQuestion[] {
  const hasCompiledPackage = (data.hoaDocuments?.length ?? 0) > 0;
  const findings = data.hoaFindings;
  const budget = data.hoaAnnualBudgetFindings;
  const rules = data.hoaOperatingRulesFindings;
  const minutes = data.hoaBoardMinutesFindings;
  const environmental = data.hoaEnvironmentalFindings;
  const missingCount = data.hoaChecklist.filter(({ status }) => status === 'NOT_RECEIVED').length;
  const pendingLitigation = (findings?.litigation ?? []).filter(
    ({ status }) => status === 'pending',
  ).length;
  const openViolations = (findings?.violations ?? []).filter(
    ({ status }) => status === 'open',
  ).length;
  const activeAssessments = (findings?.specialAssessments ?? []).filter(
    ({ status }) => status === 'active' || status === 'approved',
  ).length;
  const reservePercent = budget?.reservePercentFunded;
  const ageRestricted = rules?.ageRestrictionPresent === true;
  const minimumAge = rules?.minimumAge;
  const insurance =
    budget?.insurance || findings?.insurance
      ? { ...findings?.insurance, ...budget?.insurance }
      : undefined;
  const legalExposure = [
    ...(pendingLitigation > 0 ? [pluralize(pendingLitigation, 'pending case')] : []),
    ...(openViolations > 0 ? [pluralize(openViolations, 'open violation')] : []),
  ].join(' and ');
  const latestMeeting = [...(minutes?.meetings ?? [])]
    .filter(({ date }) => Boolean(date))
    .sort((left, right) => right.date.localeCompare(left.date))[0];

  const questions: Record<HOADueDiligenceTopic, string[]> = {
    OVERVIEW: [
      pendingLitigation > 0
        ? `What exposure could the ${pluralize(
            pendingLitigation,
            'pending litigation matter',
          )} create for this buyer?`
        : activeAssessments > 0
        ? `What is the buyer's total exposure from the ${pluralize(
            activeAssessments,
            'approved or active assessment',
          )}?`
        : reservePercent != null && reservePercent < 70
        ? `What does the reported ${reservePercent}% reserve funding conceal about this buyer's risk?`
        : 'What is the single most consequential HOA risk a careful buyer could overlook?',
      'Which material facts conflict across the HOA documents, amendments, minutes, and financial records?',
      missingCount > 0
        ? `What could be hidden by the ${pluralize(missingCount, 'checklist item')} not received?`
        : 'Which referenced exhibits, amendments, schedules, or reports are missing or stale?',
      'Which costs, restrictions, maintenance duties, or liabilities will transfer with ownership?',
      'What could affect financing, insurance eligibility, resale, or marketability even if it is not an immediate expense?',
      'What are the six highest-priority facts to verify before the buyer contingency expires?',
    ],
    FINANCIAL: [
      reservePercent != null
        ? `Is the reported ${reservePercent}% reserve funding truly current, comparable, and sufficient?`
        : 'Was a current reserve-funding percentage actually provided, and what prevents it from being verified?',
      'Does the reserve forecast depend on dues increases, investment returns, or board actions that have not been approved?',
      'Which major components are due soon, and is there enough cash to fund them without borrowing or a special assessment?',
      activeAssessments > 0 || budget?.specialAssessmentAnticipated
        ? 'What is the complete per-unit exposure from approved, active, or anticipated special assessments?'
        : 'Do the budgets, reserve study, or minutes foreshadow a special assessment that is not formally disclosed?',
      'Do the financial statements reveal operating deficits, high receivables, interfund borrowing, audit qualifications, or unusual commitments?',
      'Are current dues artificially low relative to expenses, delinquencies, inflation, insurance, and deferred maintenance?',
    ],
    INSURANCE: [
      insurance?.policyPeriodEnd
        ? `Is the master policy ending ${insurance.policyPeriodEnd} still valid, renewed, and adequate?`
        : 'Is there evidence of a current master policy—not merely an old certificate or summary?',
      'Is the property insured to current reconstruction cost, and could coinsurance or underinsurance reduce a claim payment?',
      'Which deductibles, sublimits, exclusions, or self-insured losses could be passed through to owners?',
      'Do the policy exclusions conflict with any wildfire, flood, earthquake, water, environmental, or building risks in the package?',
      'Do the documents reveal claims, cancellations, nonrenewals, premium shocks, or conditions imposed by the carrier?',
      'What additional HO-6, loss-assessment, earthquake, flood, or liability coverage should the buyer confirm with a lender and broker?',
    ],
    LEGAL: [
      pendingLitigation > 0 || openViolations > 0
        ? `Could the ${legalExposure} survive closing or create buyer liability?`
        : 'Is there any litigation, violation, claim, or enforcement history that is unresolved or ambiguously disclosed?',
      'Could any lien, judgment, settlement, attorney-fee obligation, or threatened claim lead to an owner assessment?',
      'Are amendments, recorded notices, enforcement decisions, or legal exhibits referenced but absent from the package?',
      'Do the governing documents conflict about owner rights, enforcement authority, or which document controls?',
      'Could any easement, covenant, indemnity, or maintenance agreement burden this unit after transfer?',
      'Which legal representation or disclosure should the buyer obtain in writing before releasing contingencies?',
    ],
    GOVERNANCE: [
      ageRestricted
        ? `Exactly how does the ${
            minimumAge ? `${minimumAge}+` : 'age'
          } restriction apply to occupants, guests, inheritance, and resale?`
        : 'Do any governing documents impose an age or occupancy restriction that the summary may not capture?',
      latestMeeting
        ? `What unresolved pattern appears across the board minutes through ${latestMeeting.date}?`
        : 'Were enough recent board minutes provided to identify recurring disputes, deferred work, or governance problems?',
      'Were material assessments, contracts, rule changes, or expenditures approved with the required quorum and authority?',
      'Are enforcement practices consistent with the written rules, or do the minutes suggest selective or changing enforcement?',
      'What restrictions could unexpectedly limit leasing, renovation, parking, pets, occupancy, or use of amenities?',
      'Do board turnover, vacancies, management changes, vendor relationships, or poor recordkeeping indicate operational risk?',
    ],
    SITE: [
      (environmental?.risks.length ?? 0) > 0
        ? 'Which recorded site or covenant finding creates the largest long-term obligation or resale concern?'
        : 'Were environmental covenants, monitoring obligations, and site-use restrictions actually evaluated?',
      'Are monitoring, ventilation, vapor, drainage, slope, fire, water, or other protective systems required—and who must maintain them?',
      'Do easements, access rights, landscape obligations, or off-site maintenance areas create costs or limit improvements?',
      'Do disclosed site conditions conflict with the master insurance coverage or create an uninsured exposure?',
      'Where is the maintenance boundary between the owner and HOA unclear, disputed, or unusually expensive?',
      'Which environmental reports, engineering studies, permits, closure letters, or monitoring results are referenced but missing?',
    ],
  };

  const labels: Record<HOADueDiligenceTopic, string[]> = {
    OVERVIEW: [
      "What's the biggest HOA risk a careful buyer could easily overlook?",
      'Where do the HOA documents conflict on material facts or obligations?',
      'Which referenced documents, amendments, exhibits, or reports are missing or stale?',
      'Which costs, restrictions, and liabilities transfer to the buyer with ownership?',
      'What could impair financing, insurance eligibility, resale value, or marketability?',
      'What must the buyer independently verify before the contingencies expire?',
    ],
    FINANCIAL: [
      'Is the current reserve funding truly sufficient for near-term replacement obligations?',
      'Does the reserve forecast rely on dues increases or actions not approved?',
      'Can upcoming capital projects be funded without borrowing or a special assessment?',
      'Do the records suggest a future special assessment is already taking shape?',
      'Do the financials reveal deficits, borrowing, receivables, or audit concerns?',
      "Are current dues artificially low relative to the HOA's actual obligations?",
    ],
    INSURANCE: [
      'Is the current master policy valid, adequate, and supported by complete evidence?',
      'Is the property insured to its full current cost of reconstruction?',
      'Which deductibles, sublimits, or uninsured losses could owners ultimately inherit?',
      'Do policy exclusions conflict with any known site or building risks?',
      'Do the documents signal nonrenewal, claims pressure, or future premium shocks?',
      'What additional owner coverage should the buyer confirm before closing?',
    ],
    LEGAL: [
      'Which legal claims, violations, or obligations could survive the property closing?',
      'Could litigation expenses or settlements become a future owner assessment?',
      'Which referenced amendments, notices, settlements, or legal exhibits are missing?',
      'Where do the governing documents conflict about authority or owner rights?',
      'Which recorded covenants, easements, or indemnities burden this particular unit?',
      'What should the seller or HOA confirm in writing before contingencies expire?',
    ],
    GOVERNANCE: [
      'How do age, occupancy, leasing, or guest restrictions actually apply?',
      'What unresolved operational patterns appear across the recent board minutes?',
      'Were major assessments, contracts, and rule changes properly authorized?',
      "Is actual enforcement consistent with the HOA's written rules and policies?",
      'Which rules could unexpectedly limit everyday use of this property?',
      'Do board vacancies, turnover, or recordkeeping indicate deeper operational risk?',
    ],
    SITE: [
      'Which site condition or recorded covenant creates the greatest long-term exposure?',
      'Who must maintain required monitoring, drainage, ventilation, or protective systems?',
      'Do easements or off-site maintenance duties create hidden costs or limits?',
      'Are known site and environmental risks adequately covered by insurance?',
      'Where is maintenance responsibility between the owner and HOA unclear?',
      'Which engineering, environmental, permit, or monitoring reports are missing?',
    ],
  };

  return questions[topic].map((inquiry, index) =>
    question(labels[topic][index], inquiry, hasCompiledPackage),
  );
}
